/**
 * Chart label / tooltip lookup from descrizioni_grafici_corretto.json (UI only).
 * Live data keys are often Title Case Italian ("Agevolazioni sosta disabili");
 * description JSON uses snake_case — matching normalizes both sides.
 * Also merges descrizioni_grafici_da_compilare_aggiornato.json for charts still missing text.
 */
import descriptionsBase from '../config/descrizioni_grafici_corretto.json';
import descriptionsTodo from '../config/descrizioni_grafici_da_compilare_aggiornato.json';

function isLabelEntry(value) {
  return value
    && typeof value === 'object'
    && !Array.isArray(value)
    && ('descrizione' in value || 'nome_asse_x' in value || 'nome_asse_y' in value || 'nome' in value);
}

function mergeDescriptionTrees(base, extra) {
  const out = { ...base };
  for (const [macro, subs] of Object.entries(extra || {})) {
    if (macro.startsWith('_') || !subs || typeof subs !== 'object') continue;
    out[macro] = { ...(out[macro] || {}) };
    for (const [sub, charts] of Object.entries(subs)) {
      if (sub.startsWith('_') || !charts || typeof charts !== 'object') continue;
      out[macro][sub] = { ...(out[macro][sub] || {}) };
      for (const [chartKey, entry] of Object.entries(charts)) {
        if (chartKey.startsWith('_') || !isLabelEntry(entry)) continue;
        // Prefer base if it already has a non-empty description
        const existing = out[macro][sub][chartKey];
        if (existing && String(existing.descrizione || '').trim()) continue;
        out[macro][sub][chartKey] = entry;
      }
    }
  }
  return out;
}

const descriptions = mergeDescriptionTrees(descriptionsBase, descriptionsTodo);

const MACRO_ALIASES = {
  linee_urbane: 'linee',
  linee_extraurbane: 'linee',
};

const SUB_ALIASES = {
  tipo_e_stato_fermata: 'tipo_e_stato_della_fermata',
  chiarezza_info: 'chiarezza_delle_informazioni',
};

const STOP_TOKENS = new Set([
  'di', 'della', 'delle', 'del', 'dei', 'degli', 'per', 'la', 'le', 'il', 'lo', 'i',
  'e', 'o', 'su', 'a', 'al', 'alla', 'ai', 'alle', 'con', 'da', 'dal', 'dai',
  'un', 'una', 'the', 'and', 'of', 'to',
]);

/**
 * Explicit data-key → description-key maps (any casing / spacing OK — normalized at runtime).
 * Prefer specific chart entries over aggregates when both exist.
 */
const CHART_ALIASES_RAW = {
  Punteggi: ['punteggi_dei_parametri', 'punteggi_dei_contenuti', 'qualita_della_messaggeria'],
  'Punteggi parametri': ['punteggi_dei_parametri', 'punteggi_dei_contenuti'],
  Medie: ['medie_dei_parametri', 'punteggi_dei_parametri', 'punteggi_dei_contenuti', 'qualita_della_messaggeria'],

  // Punti vendita
  'Visibilità pv': ['visibilita_dei_punti_vendita'],
  visibilita_pv: ['visibilita_dei_punti_vendita'],
  'Riconoscibilità pv': ['riconoscibilita_dei_punti_vendita'],
  riconoscibilita_pv: ['riconoscibilita_dei_punti_vendita'],
  gestione_coda: ['tempo_di_attesa'],
  spazio: ['punteggi_dei_parametri'],
  odori: ['odori', 'punteggi_dei_parametri'],
  illuminazione: ['punteggi_dei_parametri'],
  pavimento: ['punteggi_dei_parametri'],
  attrezzature: ['punteggi_dei_parametri'],
  ufficio: ['punteggi_dei_parametri'],
  supporti_attesa: ['punteggi_dei_parametri'],
  ambiente: ['punteggi_dei_parametri'],
  cura_degli_spazi: ['punteggi_dei_parametri'],
  aspetto: ['aspetto', 'punteggi_dei_parametri'],
  comunicazione: ['punteggi_dei_parametri'],
  disponibilita: ['disponibilita', 'punteggi_dei_parametri'],
  proattivita: ['proattivita', 'punteggi_dei_parametri'],
  cordialita: ['punteggi_dei_parametri'],

  // Fermate
  'Agevolazioni sosta disabili': ['agevolazioni_per_la_sosta_delle_persone_con_disabilita'],
  agevolazioni_sosta_disabili: ['agevolazioni_per_la_sosta_delle_persone_con_disabilita'],
  'Adesivi/scritte su struttura': ['adesivi_e_scritte_sulla_struttura'],
  adesiviscritte_su_struttura: ['adesivi_e_scritte_sulla_struttura'],
  'Vandalismi su aree informative/pubblicità': ['vandalismi_sulle_aree_informative_o_pubblicitarie'],
  vandalismi_su_aree_informativepubblicita: ['vandalismi_sulle_aree_informative_o_pubblicitarie'],
  'Visibilità cartello fermata': ['visibilita_del_cartello_di_fermata'],
  visibilita_cartello_fermata: ['visibilita_del_cartello_di_fermata'],
  'Riconoscibilità fermata': ['riconoscibilita_della_fermata'],
  riconoscibilita_fermata: ['riconoscibilita_della_fermata'],
  'Usura struttura': ['usura_della_struttura'],
  usura_struttura: ['usura_della_struttura'],
  'Pavimentazione pensilina': ['pavimentazione_della_pensilina'],
  pavimentazione_pensilina: ['pavimentazione_della_pensilina'],
  'Sicurezza fermata rispetto a strada': ['sicurezza_della_fermata_rispetto_alla_strada'],
  sicurezza_fermata_rispetto_a_strada: ['sicurezza_della_fermata_rispetto_alla_strada'],
  'Agevolazioni per non vedenti': ['agevolazioni_per_persone_non_vedenti'],
  agevolazioni_per_non_vedenti: ['agevolazioni_per_persone_non_vedenti'],
  'Leggibilità foglio informativo': ['leggibilita_del_foglio_informativo'],
  leggibilita_foglio_informativo: ['leggibilita_del_foglio_informativo'],
  'Bacheca info servizio': ['bacheca_informazioni_di_servizio'],
  bacheca_info_servizio: ['bacheca_informazioni_di_servizio'],
  'Lista punti vendita': ['lista_dei_punti_vendita'],
  lista_punti_vendita: ['lista_dei_punti_vendita'],
  'Sito web/app': ['sito_web_e_app'],
  'App azienda': ['sito_web_e_app'],
  'Sito web azienda': ['sito_web_e_app'],
  sito_web_e_app: ['sito_web_e_app'],
  'Info reperimento titoli': ['informazioni_sul_reperimento_dei_titoli_di_viaggio'],
  info_reperimento_titoli: ['informazioni_sul_reperimento_dei_titoli_di_viaggio'],
  'Info biglietti/abbonamenti disponibili': ['informazioni_su_biglietti_e_abbonamenti_disponibili'],
  info_biglietti_abbonamenti: ['informazioni_su_biglietti_e_abbonamenti_disponibili'],
  'Campagna comunicazione aziendale': ['campagna_di_comunicazione_aziendale'],
  campagna_comunicazione_aziendale: ['campagna_di_comunicazione_aziendale'],
  'Campagna comunicazione di terzi': ['campagna_di_comunicazione_di_terzi'],
  campagna_comunicazione_terzi: ['campagna_di_comunicazione_di_terzi'],
  'Orario e ragno': ['orario_e_ragno'],
  Assente: ['orario_e_ragno'],
  'Parziale o difficile': ['orario_e_ragno'],
  'Completa e chiara': ['orario_e_ragno'],

  // Linee
  'Biglietto a bordo': ['vendita_del_biglietto_da_parte_dellautista'],
  biglietto_a_bordo: ['vendita_del_biglietto_da_parte_dellautista'],
  'Vendita biglietti da autista': ['vendita_del_biglietto_da_parte_dellautista'],
  'Acquisto tramite emv (europay/mastercard/visa)': ['acquisto_tramite_emv'],
  'Acquisto tramite emv': ['acquisto_tramite_emv'],
  'Funzionamento obliteratrice cartacea': ['funzionamento_dellobliteratrice_cartacea'],
  funzionamento_obliteratrice_cartacea: ['funzionamento_dellobliteratrice_cartacea'],
  'Funzionamento obliteratrice elettronica': ['funzionamento_dellobliteratrice_elettronica'],
  funzionamento_obliteratrice_elettronica: ['funzionamento_dellobliteratrice_elettronica'],
  'Alimentazione mezzi': ['alimentazione_dei_mezzi'],
  alimentazione_mezzi: ['alimentazione_dei_mezzi'],
  'Stato esterno mezzo': ['stato_esterno_del_mezzo'],
  stato_esterno_mezzo: ['stato_esterno_del_mezzo'],
  'Stridore freni': ['stridore_dei_freni'],
  stridore_freni: ['stridore_dei_freni'],
  Vibrazione: ['vibrazioni'],
  vibrazione: ['vibrazioni'],
  'Stato sedili': ['stato_dei_sedili'],
  stato_sedili: ['stato_dei_sedili'],
  'Arredo interno': ['stato_degli_arredi'],
  arredo_interno: ['stato_degli_arredi'],
  Finestrini: ['stato_degli_arredi'],
  finestrini: ['stato_degli_arredi'],
  'Pulizia sedili': ['pulizia_dei_sedili'],
  pulizia_sedili: ['pulizia_dei_sedili'],
  'Pulizia arredi': ['pulizia_degli_arredi'],
  pulizia_arredi: ['pulizia_degli_arredi'],
  'Pulizia pavimentazione': ['pulizia_della_pavimentazione'],
  pulizia_pavimentazione: ['pulizia_della_pavimentazione'],
  'Pulizia vetri': ['pulizia_dei_vetri'],
  pulizia_vetri: ['pulizia_dei_vetri'],
  'Numero linea': ['numero_della_linea'],
  numero_linea: ['numero_della_linea'],
  'Regolamento viaggio': ['regolamento_di_viaggio'],
  regolamento_viaggio: ['regolamento_di_viaggio'],
  Cartellino: ['cartellino_identificativo'],
  cartellino: ['cartellino_identificativo'],
  'Erogazione servizio': ['erogazione_del_servizio'],
  erogazione_servizio: ['erogazione_del_servizio'],
  'Videocamere sorveglianza': ['videocamere_di_sorveglianza'],
  videocamere_sorveglianza: ['videocamere_di_sorveglianza'],
  'Aree attracco carrozzelle': ['aree_di_attracco_per_carrozzine'],
  aree_attracco_carrozzelle: ['aree_di_attracco_per_carrozzine'],
  'Utilizzo telefono': ['utilizzo_del_cellulare'],
  utilizzo_telefono: ['utilizzo_del_cellulare'],
  'Utilizzo auricolari': ['utilizzo_di_auricolari'],
  utilizzo_auricolari: ['utilizzo_di_auricolari'],
  'Distrazione dalla guida': ['distrazione_alla_guida'],
  distrazione_dalla_guida: ['distrazione_alla_guida'],
  'Salite/discese fuori fermata': ['salite_discese_fuori_fermata'],
  salite_discese_fuori_fermata: ['salite_discese_fuori_fermata'],
  'Adesivi utilizzo servizio': ['adesivi_per_lutilizzo_del_servizio'],
  adesivi_utilizzo_servizio: ['adesivi_per_lutilizzo_del_servizio'],
  'Scritte promozione': ['scritte_per_promozione'],
  scritte_promozione: ['scritte_per_promozione'],
  'Stato spazio pubblicitario': ['stato_dello_spazio_pubblicitario'],
  stato_spazio_pubblicitario: ['stato_dello_spazio_pubblicitario'],
  'Apertura porte': ['apertura_delle_porte'],
  apertura_porte: ['apertura_delle_porte'],

  // Call center
  'Tentativi falliti': ['linea_al_primo_tentativo'],
  tentativi_falliti: ['linea_al_primo_tentativo'],
  'Lingua inglese': ['conoscenza_della_lingua_inglese'],
  conoscenza_inglese: ['conoscenza_della_lingua_inglese'],
  'Qualità messaggeria': ['qualita_della_messaggeria'],
  qualita_messaggeria: ['qualita_della_messaggeria'],

  // Sito / App
  'Info linee percorsi': ['punteggi_dei_contenuti'],
  info_linee_percorsi: ['punteggi_dei_contenuti'],
  'Info titoli viaggio': ['punteggi_dei_contenuti'],
  info_titoli_viaggio: ['punteggi_dei_contenuti'],
  'Calcolo percorso': ['punteggi_dei_contenuti'],
  calcolo_percorso: ['punteggi_dei_contenuti'],
  'Info real time': ['punteggi_dei_contenuti', 'informazioni_real_time'],
  info_real_time: ['punteggi_dei_contenuti', 'informazioni_real_time'],
  Navigabilita: ['punteggi_dei_parametri'],
  navigabilita: ['punteggi_dei_parametri'],
  'Chiarezza info': ['punteggi_dei_parametri'],
  chiarezza_info: ['punteggi_dei_parametri'],
  Responsiveness: ['punteggi_dei_parametri'],
  responsiveness: ['punteggi_dei_parametri'],
  'Contenuti chiarezza': ['punteggi_dei_parametri'],
  contenuti_chiarezza: ['punteggi_dei_parametri'],
  'Info in tempo reale': ['informazioni_real_time'],
  'Info tempo reale': ['informazioni_real_time'],
  info_tempo_reale: ['informazioni_real_time'],
  "Indicazione all'app": ['indicazioni_sullapp'],
  'Indicazione app': ['indicazioni_sullapp'],
  indicazione_app: ['indicazioni_sullapp'],
  'Info su eventi territorio': ['informazioni_su_eventi_del_territorio'],
  info_eventi_territorio: ['informazioni_su_eventi_del_territorio'],
  'Rimando social': ['rimando_ai_social'],
  rimando_social: ['rimando_ai_social'],
  'Acquisto titoli': ['acquisto_dei_titoli_di_viaggio'],
  acquisto_titoli: ['acquisto_dei_titoli_di_viaggio'],
  'Elenco punti vendita': ['elenco_dei_punti_vendita'],
  elenco_punti_vendita: ['elenco_dei_punti_vendita'],
};

const AGGREGATE_KEYS = new Set([
  'punteggi_dei_parametri',
  'punteggi_dei_contenuti',
  'qualita_della_messaggeria',
  'tempo_di_attesa',
  'orario_e_ragno',
  'sito_web_e_app',
]);

export function normalizeChartKey(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/'/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/** Build alias map keyed by normalized form so Title Case / snake_case both hit. */
const CHART_ALIASES = (() => {
  const map = {};
  for (const [key, aliases] of Object.entries(CHART_ALIASES_RAW)) {
    const norm = normalizeChartKey(key);
    map[norm] = [...new Set([...(map[norm] || []), ...aliases])];
  }
  return map;
})();

function prettyLabel(value) {
  const s = String(value || '').trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function significantTokens(norm) {
  return norm.split('_').filter((t) => t && t.length > 1 && !STOP_TOKENS.has(t));
}

function tokensSimilar(a, b) {
  if (a === b) return true;
  if (a.length >= 4 && b.length >= 4 && (a.startsWith(b) || b.startsWith(a))) return true;
  return false;
}

function scoreKeyMatch(candidateKey, targetNorm) {
  const cand = normalizeChartKey(candidateKey);
  if (!cand || !targetNorm) return 0;
  if (cand === targetNorm) return 1;
  if (cand.includes(targetNorm) || targetNorm.includes(cand)) {
    return Math.min(cand.length, targetNorm.length) / Math.max(cand.length, targetNorm.length);
  }

  const a = significantTokens(cand);
  const b = significantTokens(targetNorm);
  if (!a.length || !b.length) return 0;

  let inter = 0;
  const used = new Set();
  for (const ta of a) {
    const idx = b.findIndex((tb, i) => !used.has(i) && tokensSimilar(ta, tb));
    if (idx >= 0) {
      used.add(idx);
      inter += 1;
    }
  }
  return inter / new Set([...a, ...b]).size;
}

function aliasesFor(rawKey) {
  if (!rawKey) return [];
  return CHART_ALIASES[normalizeChartKey(rawKey)] || [];
}

function findInObject(obj, rawKey, { allowAggregateFallback = true } = {}) {
  if (!obj || typeof obj !== 'object' || !rawKey) return null;

  if (isLabelEntry(obj[rawKey])) return obj[rawKey];

  const targetNorm = normalizeChartKey(rawKey);
  for (const [key, value] of Object.entries(obj)) {
    if (isLabelEntry(value) && normalizeChartKey(key) === targetNorm) return value;
  }

  for (const alias of aliasesFor(rawKey)) {
    if (isLabelEntry(obj[alias])) return obj[alias];
  }

  let best = null;
  let bestScore = 0;
  let aggregate = null;
  let chartCount = 0;

  for (const [key, value] of Object.entries(obj)) {
    if (!isLabelEntry(value) || key.startsWith('_')) continue;
    chartCount += 1;
    if (AGGREGATE_KEYS.has(key)) aggregate = value;

    let score = scoreKeyMatch(key, targetNorm);
    if (value.nome) score = Math.max(score, scoreKeyMatch(value.nome, targetNorm));
    if (score > bestScore) {
      bestScore = score;
      best = value;
    }
  }

  if (bestScore >= 0.45) return best;

  if (allowAggregateFallback) {
    if (chartCount === 1) {
      for (const [key, value] of Object.entries(obj)) {
        if (isLabelEntry(value) && !key.startsWith('_')) return value;
      }
    }
    if (aggregate) return aggregate;
  }

  return null;
}

function resolveMacroBucket(macro) {
  if (!macro) return null;
  if (descriptions[macro]) return descriptions[macro];
  const alias = MACRO_ALIASES[macro];
  if (alias && descriptions[alias]) return descriptions[alias];
  const norm = normalizeChartKey(macro);
  for (const key of Object.keys(descriptions)) {
    if (normalizeChartKey(key) === norm) return descriptions[key];
  }
  return null;
}

function resolveSubBucket(macroBucket, subKey) {
  if (!macroBucket || !subKey) return null;
  if (macroBucket[subKey]) return macroBucket[subKey];
  const alias = SUB_ALIASES[subKey];
  if (alias && macroBucket[alias]) return macroBucket[alias];

  const targetNorm = normalizeChartKey(subKey);
  let best = null;
  let bestScore = 0;
  for (const [key, value] of Object.entries(macroBucket)) {
    if (!value || typeof value !== 'object' || key.startsWith('_')) continue;
    const score = scoreKeyMatch(key, targetNorm);
    if (score > bestScore) {
      bestScore = score;
      best = value;
    }
  }
  return bestScore >= 0.45 ? best : null;
}

function toResult(entry) {
  if (!entry) return null;
  const descrizione = String(entry.descrizione || '').trim();
  const asseX = prettyLabel(entry.nome_asse_x || entry.asse_X || '');
  const asseY = prettyLabel(entry.nome_asse_y || entry.asse_Y || '');
  const nome = prettyLabel(entry.nome || '');
  if (!descrizione && !asseX && !asseY && !nome) return null;
  return { nome, descrizione, asseX, asseY };
}

/**
 * @param {string} [parentKey] - optional parent grafici key (nested series inherit parent labels)
 * @returns {{ nome: string, descrizione: string, asseX: string, asseY: string } | null}
 */
export function getChartLabels(macro, subKey, chartKey, parentKey) {
  const macroBucket = resolveMacroBucket(macro);
  const subBucket = resolveSubBucket(macroBucket, subKey);
  if (!subBucket) return null;

  const candidates = [
    chartKey,
    ...aliasesFor(chartKey),
  ];

  if (!chartKey || chartKey === 'Punteggi' || chartKey === 'Punteggi parametri' || chartKey === 'Medie') {
    candidates.push(
      'punteggi_dei_parametri',
      'punteggi_dei_contenuti',
      'qualita_della_messaggeria',
    );
  }

  for (const candidate of candidates) {
    const entry = findInObject(subBucket, candidate, { allowAggregateFallback: false });
    if (entry) return toResult(entry);
  }

  if (parentKey) {
    for (const candidate of [parentKey, ...aliasesFor(parentKey)]) {
      const entry = findInObject(subBucket, candidate, { allowAggregateFallback: false });
      if (entry) return toResult(entry);
    }
  }

  const fallback = findInObject(subBucket, chartKey || 'Punteggi', { allowAggregateFallback: true });
  return toResult(fallback);
}
